import React from "react";
import { IconButton } from "@mui/material";
import {
  ExpandLess,
  ExpandMore,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import { createDots } from "../../../common/createDots";

// Card shell for one alert receiver on the Alert Settings tab (ENG-2483):
// a plain-language headline saying what the receiver gets, the delivery
// options under it, and the credentials/IDs folded into "Connection details".

export const ReceiverSection = ({
  title,
  testId,
  addButton,
  children,
}: {
  title: string;
  testId: string;
  addButton?: React.ReactNode;
  children?: React.ReactNode;
}) => (
  <section className="pb-4" data-testid={testId}>
    <div className="flex min-h-[52px] items-center justify-between bg-white px-4">
      <h3 className="font-mulish text-[18px] font-bold text-black">{title}</h3>
      {addButton}
    </div>
    {children}
  </section>
);

export const AlertReceiverCard = ({
  label,
  headline,
  note,
  actions,
  testId,
  children,
}: {
  /** e.g. "Immix alert 1" */
  label: string;
  /** What this receiver gets, in words. */
  headline: string;
  /** Optional second line (what the options mean, caveats). */
  note?: string;
  actions?: React.ReactNode;
  testId: string;
  children?: React.ReactNode;
}) => (
  <div
    className="mx-4 mt-3 rounded-lg border border-[#E2E8F2] bg-white px-4 py-3 font-mulish text-actuate-blue"
    data-testid={testId}
  >
    <div className="flex flex-wrap items-start justify-between gap-2">
      <div className="min-w-0 flex-1">
        <p className="text-[12px] font-semibold uppercase tracking-wide text-[#5B6B86]">
          {label}
        </p>
        <p
          className="text-[15px] font-bold leading-snug"
          data-testid="alert-receiver-headline"
        >
          {headline}
        </p>
        {note && (
          <p
            className="mt-0.5 text-[13px] text-[#5B6B86]"
            data-testid="alert-receiver-note"
          >
            {note}
          </p>
        )}
      </div>
      {actions && <div className="flex items-center gap-1">{actions}</div>}
    </div>
    {children}
  </div>
);

export const ConnectionDetails = ({
  idPrefix,
  defaultOpen = false,
  children,
}: {
  idPrefix: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) => {
  const [open, setOpen] = React.useState(defaultOpen);
  const panelId = `${idPrefix}-connection-details`;
  return (
    <div className="mt-2 border-t border-[#E2E8F2] pt-2">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((wasOpen) => !wasOpen)}
        className="flex items-center gap-1 text-[13px] font-semibold text-[#283E6D] hover:text-actuate-orange"
      >
        {open ? (
          <ExpandLess fontSize="small" />
        ) : (
          <ExpandMore fontSize="small" />
        )}
        Connection details
      </button>
      {open && (
        <div
          id={panelId}
          className="mt-2 grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2 lg:grid-cols-3"
        >
          {children}
        </div>
      )}
    </div>
  );
};

/** One read-only label/value pair inside Connection details. */
export const DetailItem = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div className="min-w-0">
    <div className="text-[12px] font-semibold text-[#5B6B86]">{label}</div>
    <div className="break-words text-[14px]">{children}</div>
  </div>
);

/**
 * A stored secret, masked. Only users allowed to see passwords (the same
 * `view_passwords` permission the old table used) get the reveal button.
 */
export const SecretValue = ({
  value,
  canReveal,
}: {
  value: unknown;
  canReveal: boolean;
}) => {
  const [shown, setShown] = React.useState(false);
  if (!value) return <>--</>;
  const secret = String(value);
  return (
    <span className="flex items-center gap-1">
      <span data-testid="secret-value">
        {canReveal && shown ? secret : createDots(secret)}
      </span>
      {canReveal && (
        <IconButton
          size="small"
          aria-label={shown ? "hide the password" : "display the password"}
          onClick={() => setShown((wasShown) => !wasShown)}
          onMouseDown={(event) => event.preventDefault()}
        >
          {shown ? (
            <VisibilityOff fontSize="small" />
          ) : (
            <Visibility fontSize="small" />
          )}
        </IconButton>
      )}
    </span>
  );
};
